import React, { useCallback } from 'react';
import { Grid } from '~/components/';
import { useForm } from '~/hooks';
import { MdAddCircle } from 'react-icons/md';
import { FaPencilAlt, FaTrash} from 'react-icons/fa';
import { useSectionLoader, useSaveSectionDetail, useDeleteSectionDetail } from '../../hooks';
import sectionTypes from '../sectionTypes';
import LenderLookup from './LenderLookup';
import { sortStrings } from '~/util/sorters';
import { addressDetails } from '~/api';


/**
 * @typedef LenderValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId 
 * @property {object}   lenderType 
 * @property {object}   lenderPrimary 
 * @property {object}   lenderSecondary 
 * @property {[object]} lenderTypeOptions 
 * @property {[object]} sectionDetails
 * @property {object}   relationship
 * @property {[object]} relationshipOptions
 * @property {boolean}  addLenderFlag
 * 
 * 
 * @typedef FormState
 * @property {LenderValues} values The current form values
 */

/** @type {LenderValues} */

const defaultState = {
    id: 0,
    sectionId: 0,
    stateOptions: [],
    lenderType: { label: '...', value: '' },
    lenderTypeOptions: [],
    relationship: { label: '...', value: '' },
    relationshipOptions: [],
    sectionDetails: [],
    data: [],
    addLenderModalFLag: false,
    lenderDetailData: {},
    editLender: false,
    sectionType: sectionTypes.TITLE_INSURANCE_APPLICATION
};

const loadData = async (values) => {
    const defaultLenderType = defaultState.lenderTypeOptions.find(s => s.id == 1);
    const stateOptions = await addressDetails.getStateCodes();
    return {
        ...values,
        stateOptions,
        sectionId: values ? values.sectionId: 0,
        lenderPrimaryType: values? values.lenderPrimaryType : defaultLenderType,
        lenderSecondaryType: values? values.lenderSecondaryType :  defaultLenderType
    };
};

const Lender = (props) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.TITLE_INSURANCE_APPLICATION, props.titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadData(values).then(loadSection(values));
    }, [props.sectionDetails]);

    const lenderDetail =   (props.sectionDetails && Object.keys(props.sectionDetails).length > 0) ? props.sectionDetails.map((detail) => {
        return {
            ...detail,
            id: detail.sectionDetailId,
            lenderName: detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderName : '',
            lenderStreetAddress: detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderStreetAddress: '',
            lenderCity: detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderCity : '',
            lenderState: detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderState : '',
            lenderZip: detail._data.lenderDetailData ? detail._data.lenderDetailData.lenderZip : ''       
        } ;
    }) : [];

    const { state, fieldActions } = useForm({
        initialState: {...defaultState},
        onLoad,
        debug: true 
    });    

    const { deleteSectionDetail } = useDeleteSectionDetail(state.values.data, state.values.sectionDetailId);

    const onEditClick = async (data) => {
        await fieldActions.onChange(data._data.lenderDetailData, 'lenderDetailData');
        await fieldActions.onChange(true, 'editLender');
        fieldActions.onChange(true, 'addLenderModalFLag');
    };

    const onDeleteClick = (id) => {
        deleteSectionDetail(id);
    };

    const addLenderModal = () => {
        fieldActions.onChange(false, 'editLender');
        fieldActions.onChange(true, 'addLenderModalFLag');
    };

    const cancelAddLender = () => {
        fieldActions.onChange(false, 'addLenderModalFLag');
    }; 

    const { saveField } = useSaveSectionDetail(state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
    
    const addLender = async (values) => {    
        const data = {
            sectionId: props.sectionId,
            type: props.lenderType,
            lenderDetailData: values          
        };

        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, state.values.sectionDetailId);
        await fieldActions.onChange(false, 'addLenderModalFLag');
        
    };

    const saveLender = async (values) => {
        const data = {
            sectionId: props.sectionId,
            sectionDetailId: props.sectionDetails[0].sectionDetailId,
            type: props.lenderType,
            lenderDetailData: values.lenderDetailData          
        };

        
        await fieldActions.onChange(data, 'data');
        await saveField( state.values.data, state.values.sectionType, props.initialDefaultData, props.titleOpinionId, data.sectionDetailId);
        fieldActions.onChange(false, 'addLenderModalFLag');
    };

    const columns = [
        {
            title:'Name*',
            width: 500,
            field: 'lenderName'
        },
        {
            title:'Address*',
            width: 700,
            sorter: (a,b) => {
                return sortStrings(
                    `${a.lenderStreetAddress} ${a.lenderCity} ${a.lenderState} ${a.lenderZip}`,
                    `${b.lenderStreetAddress} ${b.lenderCity} ${b.lenderState} ${b.lenderZip}`
                );
            },
            cell: ({data}) => {
                return (
                    <>                    
                        <div>{data.lenderStreetAddress + ' ' + data.lenderCity + ' ' + data.lenderState + ' ' + data.lenderZip}</div>
                    </>
                );
            },
        },
        {
            field: 'edit',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>
                        <button className="btn btn-link rightSpace" 
                            onClick={() => onEditClick(data)}>
                            <FaPencilAlt />
                        </button>
                    </>
                );
            }
        },
        {
            field: 'delete',
            width: 150,
            canSort: false,
            cell: ({ data }) => {
                return (
                    <>                    
                        <button className="btn btn-link rightSpace" 
                            onClick={() => onDeleteClick(data.id)}>
                            <FaTrash />
                        </button>
                    </>
                );
            }
        }
    ];

    return (
        <React.Fragment>
            <div className="col-span-12">   
                {lenderDetail ?          
                    <Grid
                        className="grid-stretch"
                        columns={columns}
                        data={lenderDetail}
                    />
                    : <></>}
                <LenderLookup
                    isModalOpen={state.values.addLenderModalFLag}
                    lenderDetailData={state.values.lenderDetailData}
                    stateOptions={state.values.stateOptions}
                    editLender={state.values.editLender}
                    onCancel={() => cancelAddLender()}
                    headerTxt={'Lender Lookup'}
                    sectionId={props.sectionId}
                    addLender={(values) => addLender(values)}
                    saveLender={(values) => saveLender(values)}
                />
            </div>
            {props.sectionDetails.length > 0 || !props.editMode ?
                <div className='mt-auto col-span-12'></div>
                :
                <div className='mt-auto col-span-12'>
                    <div className="flex justify-start items-center mt-auto">
                        <button
                            className='py-2 px-2 rounded-full text-white flex items-center focus:outline-none bg-blue-60 disabled:opacity-50'
                            onClick={() => addLenderModal()}
                        >
                            <MdAddCircle className="mr-2" size="1.25rem" />
                            Add Lender
                        </button>
                    </div>
                </div>
            }
            
        </React.Fragment>
    );
};

export default Lender;